import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import { resolveAssetOpeningTimesForDisplay } from '../api/assetPayloadNormalize';
import {
  catalogKindFromRow,
  labelWithCurrencySymbol,
  localizeAssetFieldLabel,
  localizeAssetFieldValue,
  localizeAssetPriceFieldLabel,
  localizeAssetTypeLabel,
  localizeCatalogDisplayName,
  localizeFacilityAddonType,
  localizeGeoLabel,
  resolveAssetLocaleCurrency,
  useI18n,
} from '../i18n';
import { fetchAssetFacilities, fetchAssetTypesForBrowse } from '../api/getAssetById';
import type { AssetLocaleCurrency } from '../utils/assetCountryCurrency';
import { formatMoneyAmount, isPriceRelatedFieldKey } from '../utils/assetCountryCurrency';
import type { TranslateFn } from '../i18n/operationLabels';
import type { AppLanguage } from '../i18n/types';
import { AssetImageDistanceOverlay } from './AssetImageDistanceOverlay';
import { AssetFacilityIcon } from './AssetFacilityIcon';
import { AssetImageHeroCarousel } from './AssetImageHeroCarousel';
import { ImageLightbox } from './ImageLightbox';
import { AssetTypeIcon } from './AssetTypeIcon';
import { FavoriteAssetHeartButton } from '../favorites/FavoriteAssetHeartButton';
import { ASSET_OFFICE_PLACEHOLDER_SRC } from '../constants/assetOfficePlaceholder';
import { pickFacilityAddonHighlights } from '../utils/assetFacilityIcons';
import {
  enrichFacilitiesWithCatalog,
  mergeFacilitiesAndPromotedAddonsForDisplay,
} from '../utils/assetPromotedFacilities';
import { resolveListingHourlyDailyPrices } from '../utils/assetBookingPricingOptions';
import {
  assetListingEmail,
} from '../utils/assetListingDisplay';
import {
  coerceImagesWireValue,
  extractImagePathsFromRecordImagesField,
  extractImageUrlStrings,
  prepareRecordForThumbnailLookup,
  readRecordImagesWireValue,
  resolveAssetImageSrc,
} from '../utils/assetImageResolve';
import { OWNER_LOGO_KEYS } from '../utils/ownerLogoFromRecord';
import {
  flattenOpeningTimesToDailyDisplayRows,
  formatOpeningTimesDayGroupLines,
  groupOpeningTimesWeekdayDisplayRows,
} from '../utils/openingTimesDisplayRows';
import { OPENING_TIMES_WEEKDAY_ORDER, isOpeningTimesWeekdayKey } from '../utils/openingTimesWeekdays';
import {
  resolveAssetRecordLogoSrc,
  type AssetRecordLogoOptions,
} from '../utils/assetRecordOwnerLogo';
import { assetRecordPrimaryId } from '../utils/assetRecordPrimaryId';
import { assetRecordShortDisplayId } from '../utils/assetShortId';
import { assetRecordSubTitle } from '../utils/assetHomeCardSubtitle';
import { coalesceAssetListRecord } from '../utils/assetListNormalize';
import { buildAssetBrowseShareUrl } from '../navigation/appHistory';
import {
  canUseSystemShareSheet,
  copyShareText,
  shareContent,
  shareViaWhatsApp,
} from '../utils/nativeShare';
import styles from './AssetGetByIdView.module.css';

export { assetRecordPrimaryId };

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/** API list payloads often omit `images[]` and use a scalar path/URL on the row. */
const IMAGE_SCALAR_KEYS: readonly string[] = [
  'image',
  'image_url',
  'ImageUrl',
  'asset_image',
  'asset_image_url',
  'asset_thumbnail',
  'asset_thumbnail_url',
  'thumbnail',
  'thumbnail_url',
  'thumbnailUrl',
  'thumb_url',
  'cover_image',
  'cover_image_url',
  'coverImage',
  'coverImageUrl',
  'photo',
  'photo_url',
  'picture',
  'banner',
  'avatar',
  'icon',
  'main_image',
  'primary_image',
  'image_path',
  'imagePath',
  'facility_image',
  'facility_image_url',
];

function isImageDetailKey(key: string): boolean {
  if (key === 'images' || key === 'image_urls' || key === 'photos') {
    return true;
  }
  if ((IMAGE_SCALAR_KEYS as readonly string[]).includes(key)) {
    return true;
  }
  return /image|img|thumbnail|thumb|photo|picture|banner|cover|avatar|poster|logo|splash|media|graphic/i.test(
    key,
  );
}

/** Require URL-like/path-like values so unrelated string fields aren’t mistaken for paths. */
function stringLooksLikeImageReference(s: string): boolean {
  const t = s.trim();
  return (
    /^https?:\/\//i.test(t) ||
    t.startsWith('data:image/') ||
    t.startsWith('blob:') ||
    /^\/.+\.(jpe?g|png|gif|webp|svg|bmp|avif)(\?|#|$)/i.test(t) ||
    /\.(jpe?g|png|gif|webp|svg|bmp|avif)(\?|#|$)/i.test(t.split(/[/\\]/).pop() ?? '')
  );
}

function mergeRawImageCandidates(row: Record<string, unknown>, excludePaths?: ReadonlySet<string>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  const add = (s: string) => {
    const t = s.trim();
    if (!t || seen.has(t) || excludePaths?.has(t)) {
      return;
    }
    seen.add(t);
    out.push(t);
  };

  extractImageUrlStrings(row.images).forEach(add);
  extractImageUrlStrings(row['image_urls']).forEach(add);
  extractImageUrlStrings(row['photos']).forEach(add);

  for (const k of IMAGE_SCALAR_KEYS) {
    if ((OWNER_LOGO_KEYS as readonly string[]).includes(k)) {
      continue;
    }
    const v = row[k];
    if (typeof v === 'string') {
      add(v);
    }
  }

  for (const [k, v] of Object.entries(row)) {
    if (typeof v !== 'string' || !v.trim()) {
      continue;
    }
    if ((OWNER_LOGO_KEYS as readonly string[]).includes(k as (typeof OWNER_LOGO_KEYS)[number])) {
      continue;
    }
    const keyGuess =
      /image|img|thumbnail|thumb|photo|picture|banner|cover|avatar|poster|splash|media|graphic/i.test(k);
    if (!keyGuess || /logo/i.test(k)) {
      continue;
    }
    if (!stringLooksLikeImageReference(v)) {
      continue;
    }
    add(v.trim());
  }

  return out;
}

/** Raw path/URL strings in display order — list rows embed images under varied keys/nested asset. */
function collectRecordImageRawPaths(record: Record<string, unknown>): string[] {
  const prepared = prepareRecordForThumbnailLookup(coalesceAssetListRecord(record));
  const logoExclude = new Set<string>();
  for (const key of OWNER_LOGO_KEYS) {
    const v = prepared[key];
    if (typeof v === 'string' && v.trim()) {
      logoExclude.add(v.trim());
    }
  }
  for (const ownerKey of ['asset_owner', 'owner', 'private_asset_owner', 'business_asset_owner'] as const) {
    const nested = prepared[ownerKey];
    if (!isPlainObject(nested)) {
      continue;
    }
    for (const key of OWNER_LOGO_KEYS) {
      const v = nested[key];
      if (typeof v === 'string' && v.trim()) {
        logoExclude.add(v.trim());
      }
    }
  }

  const fromImagesField = extractImagePathsFromRecordImagesField(prepared);
  if (fromImagesField !== null && fromImagesField.length > 0) {
    return fromImagesField.filter((path) => !logoExclude.has(path.trim()));
  }

  const seen = new Set<string>();
  const out: string[] = [];
  const appendFromRow = (row: Record<string, unknown>) => {
    for (const s of mergeRawImageCandidates(row, logoExclude)) {
      if (seen.has(s)) {
        continue;
      }
      seen.add(s);
      out.push(s);
    }
  };

  appendFromRow(prepared);
  for (const nestedKey of ['asset', 'Asset'] as const) {
    const nested = prepared[nestedKey];
    if (isPlainObject(nested)) {
      appendFromRow(nested);
    }
  }
  return out;
}

function recordResolvedImageSrcs(record: Record<string, unknown>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of collectRecordImageRawPaths(record)) {
    const src = resolveAssetImageSrc(raw);
    if (!src || seen.has(src)) {
      continue;
    }
    seen.add(src);
    out.push(src);
  }
  return out;
}

/** Unique resolved `<img src>` URLs for an asset record (API base joined for relative paths). */
export function assetRecordResolvedImageSrcs(record: Record<string, unknown>): string[] {
  return recordResolvedImageSrcs(record);
}

/** Raw path/URL strings found on the record before resolution (for diagnostics). */
export function assetRecordRawImageCandidates(record: Record<string, unknown>): string[] {
  return collectRecordImageRawPaths(record);
}

function coerceCapacityNumber(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Single-line capacity fragment, e.g. `(10 - 200)`, or `null` when both min and max are absent.
 */
export function assetRecordCapacityLine(data: Record<string, unknown>): string | null {
  const min = coerceCapacityNumber(data.min_capacity);
  const max = coerceCapacityNumber(data.max_capacity);
  if (min === null && max === null) {
    return null;
  }
  return `(${min !== null ? String(min) : '—'} - ${max !== null ? String(max) : '—'})`;
}

/**
 * Explains payload issues when `images` exists but no paths could be extracted (shape / missing fields).
 */
export function assetRecordImagePayloadNotice(record: Record<string, unknown>): string | null {
  const wire = readRecordImagesWireValue(record);
  if (wire === undefined || wire === null) {
    return null;
  }
  const coerced = coerceImagesWireValue(wire);
  const imagesArr = Array.isArray(coerced) ? coerced : [];
  const raw = collectRecordImageRawPaths(record);
  if (imagesArr.length === 0 || raw.length > 0) {
    return null;
  }
  return 'This asset includes an images field, but no usable URL or path could be read from those entries. Each item should be a string path/URL or an object with url, src, image_url, or path. If uploads are stored only on disk, the API must expose a URL or web path.';
}

function formatScalar(value: unknown, t: TranslateFn): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? t('common.yes') : t('common.no');
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '—';
  }
  if (typeof value === 'string') {
    return value.trim() || '—';
  }
  return JSON.stringify(value);
}

function formatAssetRegistrationDate(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }
  const dt = new Date(value.trim());
  if (Number.isNaN(dt.getTime())) {
    return null;
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'long' }).format(dt);
}

function formatAssetDetailValue(
  key: string,
  value: unknown,
  t: TranslateFn,
  language: AppLanguage,
  currency: AssetLocaleCurrency,
): string {
  if (key === 'registration_date' || key === 'registered_at') {
    const formatted = formatAssetRegistrationDate(value);
    if (formatted !== null) {
      return formatted;
    }
  }
  if (isPriceRelatedFieldKey(key)) {
    const n = parseNumericLiteral(value);
    if (n !== null) {
      return formatMoneyAmount(n, currency);
    }
  }
  const localized = localizeAssetFieldValue(key, value, language, t);
  if (localized !== null) {
    return localized;
  }
  return formatScalar(value, t);
}

/** Readable numbers with thousand separators (e.g. 1,234.56) for monetary / numeric table cells. */
function formatGroupedNumber(n: number): string {
  if (!Number.isFinite(n)) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 20 }).format(n);
}

function parseNumericLiteral(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const t = value.trim();
    if (t !== '' && /^-?\d+(\.\d+)?$/.test(t)) {
      const n = Number(t);
      return Number.isFinite(n) ? n : null;
    }
  }
  return null;
}

function looksLikePriceColumnKey(k: string): boolean {
  const lower = k.toLowerCase().replace(/^[_]+/, '');
  if (/(^|_)price$/.test(lower) || /^price\b/.test(lower)) {
    return true;
  }
  return lower === 'amount' || lower === 'cost' || lower === 'rate' || lower === 'fee' || lower === 'unit_price';
}

/** Foreign-key style columns omitted from Facilities / Pricing tables (details stay in Raw JSON). */
function isHiddenCodeColumnKey(columnKey: string): boolean {
  const k = columnKey.toLowerCase().replace(/^[_]+/, '');
  const hidden = [
    'addon_id',
    'facility_id',
    'pricing_type_id',
    'facility_type_id',
    'id',
  ];
  return hidden.includes(k) || columnKey === 'ID';
}

function formatPricingTableCell(
  columnKey: string,
  value: unknown,
  t: TranslateFn,
  language: AppLanguage,
  currency: AssetLocaleCurrency,
): string {
  if (columnKey === ITEM_TYPE_KEY) {
    return localizeFacilityAddonType(value, t);
  }
  if (columnKey === ITEM_KEY) {
    return typeof value === 'string' && value.trim() ? value.trim() : formatScalar(value, t);
  }
  const localized = localizeAssetFieldValue(columnKey, value, language, t);
  if (localized !== null) {
    return localized;
  }
  if (looksLikePriceColumnKey(columnKey)) {
    const n = parseNumericLiteral(value);
    if (n !== null) {
      return formatMoneyAmount(n, currency);
    }
  }
  return formatScalar(value, t);
}

const TOP_FIELD_ORDER = [
  'id',
  'asset_id',
  'asset_name',
  'name',
  'description',
  'email',
  'phone_number',
  'registration_date',
  'created_at',
  'updated_at',
  'state_id',
  'country_id',
  'asset_type_id',
  'latitude',
  'longitude',
];

const NESTED_SECTION_KEYS = new Set([
  'address',
  'opening_times',
  'opening_times_json',
  'openingTimesJson',
  'facilities',
  'pricing_methods',
  'addons',
  'images',
]);

function sortedKeys(obj: Record<string, unknown>): string[] {
  const keys = Object.keys(obj);
  const primary = TOP_FIELD_ORDER.filter((k) => keys.includes(k));
  const rest = keys.filter((k) => !TOP_FIELD_ORDER.includes(k) && !NESTED_SECTION_KEYS.has(k));
  rest.sort();
  return [...primary, ...rest];
}

function AddressBlock({
  address,
  zipFallback,
}: {
  address: Record<string, unknown>;
  zipFallback?: Record<string, unknown>;
}) {
  const { t, language } = useI18n();
  const lines: string[] = [];
  const street = address.street;
  const building = address.building ?? address.street_number ?? address.house_number;
  const line2 = address.address2 ?? address.address_2;
  const city = address.city;
  const zip =
    pickAddressZip(address) || (zipFallback ? pickAddressZip(zipFallback) : null);
  const state = address.state ?? address.state_name;
  const country = address.country ?? address.country_name;

  if (typeof street === 'string' && street.trim()) {
    const buildingPart = typeof building === 'string' ? building.trim() : '';
    lines.push(buildingPart ? `${street.trim()} ${buildingPart}` : street.trim());
  }
  if (typeof line2 === 'string' && line2.trim()) {
    lines.push(line2.trim());
  }
  const cityZip: string[] = [];
  if (typeof city === 'string' && city.trim()) {
    cityZip.push(city.trim());
  }
  if (zip) {
    cityZip.push(zip);
  }
  if (cityZip.length) {
    lines.push(cityZip.join(', '));
  }
  if (typeof state === 'string' && state.trim()) {
    lines.push(localizeGeoLabel(state.trim(), language));
  }
  if (typeof country === 'string' && country.trim()) {
    lines.push(localizeGeoLabel(country.trim(), language));
  }

  if (lines.length === 0) {
    return (
      <div className={styles.subsection}>
        <h3 className={styles.subsectionTitle}>{t('assetDetail.address')}</h3>
        <pre className={styles.jsonFallback}>{JSON.stringify(address, null, 2)}</pre>
      </div>
    );
  }

  return (
    <div className={styles.subsection}>
      <h3 className={styles.subsectionTitle}>{t('assetDetail.address')}</h3>
      <address className={styles.address}>
        {lines.map((line) => (
          <span key={line} className={styles.addressLine}>
            {line}
          </span>
        ))}
      </address>
    </div>
  );
}

/** Same semantics as create form full day (see DynamicRecordForm OPEN_ALL_DAY_HOURS). */
function parseLeadingHm(s: string): { h: number; m: number } | null {
  const t = s.trim();
  const m = /^(\d{1,2}):(\d{2})/.exec(t);
  if (!m) {
    return null;
  }
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (!Number.isFinite(h) || !Number.isFinite(min)) {
    return null;
  }
  return { h, m: min };
}

function isOpenAllDayMidnightToEnd(openS: string, closeS: string): boolean {
  const open = parseLeadingHm(openS);
  const close = parseLeadingHm(closeS);
  return (
    open !== null &&
    close !== null &&
    open.h === 0 &&
    open.m === 0 &&
    close.h === 23 &&
    close.m === 59
  );
}

function formatOpeningHoursValue(v: unknown, t: TranslateFn): string {
  if (v === 'closed' || v === '') {
    return t('assetDetail.closed');
  }
  if (isPlainObject(v)) {
    const o = v as Record<string, unknown>;
    if ('closed' in o && !('open' in o) && !('close' in o)) {
      const c = o.closed;
      if (!(c === false || c === 'false' || c === 0)) {
        return t('assetDetail.closed');
      }
    }
    if (typeof o.open === 'string' && typeof o.close === 'string') {
      const openS = o.open.trim();
      const closeS = o.close.trim();
      if (openS === '' && closeS === '') {
        return t('assetDetail.closed');
      }
      if (isOpenAllDayMidnightToEnd(o.open, o.close)) {
        return t('assetDetail.open24h');
      }
      return `${o.open} – ${o.close}`;
    }
  }
  return formatScalar(v, t);
}

function formatOpeningTimesRowHours(rawValue: unknown, t: TranslateFn): string {
  if (rawValue === undefined) {
    return '—';
  }
  return formatOpeningHoursValue(rawValue, t);
}

function CompactOpeningHours({
  openingTimes,
  className,
}: {
  openingTimes: Record<string, unknown>;
  className?: string;
}) {
  const { t, language } = useI18n();
  const dailyRows = flattenOpeningTimesToDailyDisplayRows(openingTimes);
  const sourceRows =
    dailyRows.length > 0 ? dailyRows : groupOpeningTimesWeekdayDisplayRows(openingTimes, language);
  const rows = sourceRows.map((row) => ({
    rowKey: row.days.join(','),
    dayLines: formatOpeningTimesDayGroupLines(row.days, t, language),
    hours: formatOpeningTimesRowHours(row.rawValue, t),
  }));
  if (rows.length === 0) {
    return null;
  }
  return (
    <ul className={[styles.compactHoursList, className].filter(Boolean).join(' ')}>
      {rows.map((row) => (
        <li key={row.rowKey} className={styles.compactHoursRow}>
          <span className={styles.compactHoursDay}>
            {row.dayLines.map((line, index) => (
              <span key={index} className={styles.compactHoursDayLine}>
                {line}
              </span>
            ))}
          </span>
          <span className={styles.compactHoursTime}>{row.hours}</span>
        </li>
      ))}
    </ul>
  );
}

function buildAssetEmailCompose(
  title: string,
  addressLine: string | null,
  shareUrl?: string | null,
): { subject: string; body: string } {
  const bodyParts: string[] = [];
  if (addressLine) {
    bodyParts.push(addressLine);
  }
  const link =
    shareUrl?.trim() ||
    (typeof window !== 'undefined' ? window.location.href : '');
  if (link) {
    bodyParts.push(link);
  }
  return {
    subject: title,
    body: bodyParts.join('\n\n'),
  };
}

function assetRecordMailtoUrl(
  email: string,
  title: string,
  addressLine: string | null,
  shareUrl?: string | null,
): string {
  const { subject, body } = buildAssetEmailCompose(title, addressLine, shareUrl);
  const params = new URLSearchParams();
  params.set('subject', subject);
  if (body) {
    params.set('body', body);
  }
  const query = params.toString();
  return query ? `mailto:${email}?${query}` : `mailto:${email}`;
}

function assetRecordGmailComposeUrl(
  email: string,
  title: string,
  addressLine: string | null,
  shareUrl?: string | null,
): string {
  const { subject, body } = buildAssetEmailCompose(title, addressLine, shareUrl);
  const params = ['view=cm', 'fs=1', `to=${encodeURIComponent(email)}`, `su=${encodeURIComponent(subject)}`];
  if (body) {
    params.push(`body=${encodeURIComponent(body)}`);
  }
  return `https://mail.google.com/mail/u/0/?${params.join('&')}`;
}

function openGmailCompose(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

function AssetProfileActionIcons({
  title,
  email,
  addressLine,
  navUrl,
  shareUrl,
  shareLabel,
  shareCopyLabel,
  shareWhatsAppLabel,
  shareEmailLabel,
  shareCopiedLabel,
  navigateLabel,
  emailLabel,
  emailGmailLabel,
  emailAppLabel,
}: {
  title: string;
  email: string | null;
  addressLine: string | null;
  navUrl: string | null;
  shareUrl: string;
  shareLabel: string;
  shareCopyLabel: string;
  shareWhatsAppLabel: string;
  shareEmailLabel: string;
  shareCopiedLabel: string;
  navigateLabel: string;
  emailLabel: string;
  emailGmailLabel: string;
  emailAppLabel: string;
}) {
  const [emailMenuOpen, setEmailMenuOpen] = useState(false);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const emailMenuRef = useRef<HTMLDivElement>(null);
  const shareMenuRef = useRef<HTMLDivElement>(null);
  const mailtoUrl = email ? assetRecordMailtoUrl(email, title, addressLine, shareUrl) : '';
  const gmailUrl = email ? assetRecordGmailComposeUrl(email, title, addressLine, shareUrl) : '';
  const sharePayload = [title, addressLine, shareUrl].filter(Boolean).join('\n');
  const shareMailtoHref = (() => {
    const params = new URLSearchParams();
    params.set('subject', title);
    if (sharePayload) {
      params.set('body', sharePayload);
    }
    return `mailto:?${params.toString()}`;
  })();

  useEffect(() => {
    if (!emailMenuOpen && !shareMenuOpen) {
      return;
    }
    const onPointerDown = (event: globalThis.MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (emailMenuOpen && emailMenuRef.current && !emailMenuRef.current.contains(target)) {
        setEmailMenuOpen(false);
      }
      if (shareMenuOpen && shareMenuRef.current && !shareMenuRef.current.contains(target)) {
        setShareMenuOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setEmailMenuOpen(false);
        setShareMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [emailMenuOpen, shareMenuOpen]);

  const onShare = async () => {
    setEmailMenuOpen(false);
    // Capacitor / browsers with Web Share API → system sheet with all apps.
    if (canUseSystemShareSheet()) {
      const url = shareUrl || (typeof window !== 'undefined' ? window.location.href : '');
      const text = [title, addressLine].filter(Boolean).join('\n');
      await shareContent({ title, text, url });
      return;
    }
    // Desktop browsers without a share sheet → in-app menu (Copy / WhatsApp Web / Email).
    setShareCopied(false);
    setShareMenuOpen((open) => !open);
  };

  const onShareCopy = async () => {
    const ok = await copyShareText(sharePayload || shareUrl);
    if (ok) {
      setShareCopied(true);
      window.setTimeout(() => {
        setShareCopied(false);
        setShareMenuOpen(false);
      }, 900);
    }
  };

  const onShareWhatsApp = () => {
    setShareMenuOpen(false);
    shareViaWhatsApp(sharePayload || shareUrl);
  };

  const onEmailClick = () => {
    if (!email) {
      return;
    }
    setShareMenuOpen(false);
    setEmailMenuOpen((open) => !open);
  };

  const onGmailClick = () => {
    if (!gmailUrl) {
      return;
    }
    setEmailMenuOpen(false);
    openGmailCompose(gmailUrl);
  };

  const navigateIcon = (
    <svg viewBox="0 0 24 24" className={styles.profileActionIcon} aria-hidden>
      <path
        d="M12 2a7 7 0 00-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 00-7-7zm0 9.5A2.5 2.5 0 1112 6a2.5 2.5 0 010 5.5z"
        fill="currentColor"
      />
    </svg>
  );

  const shareIcon = (
    <svg viewBox="0 0 24 24" className={styles.profileActionIcon} aria-hidden>
      <circle cx="6" cy="12" r="2" fill="currentColor" />
      <circle cx="18" cy="6" r="2" fill="currentColor" />
      <circle cx="18" cy="18" r="2" fill="currentColor" />
      <path d="M8 11l8-4M8 13l8 4" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );

  const emailIcon = (
    <svg viewBox="0 0 24 24" className={styles.profileActionIcon} aria-hidden>
      <path
        d="M4 6h16a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2zm0 2v.01L12 13l8-4.99V8H4zm0 8h16v-5.01l-7.52 4.7a1 1 0 01-1.04 0L4 10.99V16z"
        fill="currentColor"
      />
    </svg>
  );

  return (
    <div className={styles.profileActions}>
      {navUrl ? (
        <a
          className={styles.profileActionBtn}
          href={navUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={navigateLabel}
        >
          {navigateIcon}
        </a>
      ) : (
        <span className={[styles.profileActionBtn, styles.profileActionBtnDisabled].join(' ')} aria-hidden>
          {navigateIcon}
        </span>
      )}
      <div className={styles.profileEmailWrap} ref={shareMenuRef}>
        <button
          type="button"
          className={styles.profileActionBtn}
          onClick={() => void onShare()}
          aria-label={shareLabel}
          aria-expanded={shareMenuOpen}
          aria-haspopup="menu"
        >
          {shareIcon}
        </button>
        {shareMenuOpen ? (
          <div className={styles.profileEmailMenu} role="menu" aria-label={shareLabel}>
            <button
              type="button"
              role="menuitem"
              className={styles.profileEmailMenuItem}
              onClick={() => void onShareCopy()}
            >
              {shareCopied ? shareCopiedLabel : shareCopyLabel}
            </button>
            <button
              type="button"
              role="menuitem"
              className={styles.profileEmailMenuItem}
              onClick={onShareWhatsApp}
            >
              {shareWhatsAppLabel}
            </button>
            <a
              role="menuitem"
              className={styles.profileEmailMenuItem}
              href={shareMailtoHref}
              onClick={() => setShareMenuOpen(false)}
            >
              {shareEmailLabel}
            </a>
          </div>
        ) : null}
      </div>
      {email ? (
        <div className={styles.profileEmailWrap} ref={emailMenuRef}>
          <button
            type="button"
            className={styles.profileActionBtn}
            onClick={onEmailClick}
            aria-label={emailLabel}
            aria-expanded={emailMenuOpen}
            aria-haspopup="menu"
          >
            {emailIcon}
          </button>
          {emailMenuOpen ? (
            <div className={styles.profileEmailMenu} role="menu" aria-label={emailLabel}>
              <button
                type="button"
                role="menuitem"
                className={styles.profileEmailMenuItem}
                onClick={onGmailClick}
              >
                {emailGmailLabel}
              </button>
              <a
                role="menuitem"
                className={styles.profileEmailMenuItem}
                href={mailtoUrl}
                onClick={() => setEmailMenuOpen(false)}
              >
                {emailAppLabel}
              </a>
            </div>
          ) : null}
        </div>
      ) : (
        <span className={[styles.profileActionBtn, styles.profileActionBtnDisabled].join(' ')} aria-hidden>
          {emailIcon}
        </span>
      )}
    </div>
  );
}

function OpeningTimesBlock({ openingTimes }: { openingTimes: Record<string, unknown> }) {
  const { t, language } = useI18n();
  const weeklyRows = groupOpeningTimesWeekdayDisplayRows(openingTimes, language).map((row) => ({
    rowKey: row.days.join(','),
    dayLines: formatOpeningTimesDayGroupLines(row.days, t, language),
    hours: formatOpeningTimesRowHours(row.rawValue, t),
  }));

  const specialKeys = Object.keys(openingTimes)
    .filter((k) => !isOpeningTimesWeekdayKey(k))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  return (
    <div className={styles.subsection}>
      <h3 className={styles.subsectionTitle}>{t('assetDetail.openingTimes')}</h3>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t('assetDetail.day')}</th>
              <th>{t('assetDetail.hours')}</th>
            </tr>
          </thead>
          <tbody>
            {weeklyRows.map((row) => (
              <tr key={row.rowKey}>
                <td>
                  {row.dayLines.map((line, index) => (
                    <span key={index} className={styles.openingTimesDayLine}>
                      {line}
                    </span>
                  ))}
                </td>
                <td>{row.hours}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {specialKeys.length > 0 ? (
        <details className={styles.openingTimesSpecialDatesDetails}>
          <summary className={styles.openingTimesSpecialDatesSummary}>
            {t('assetDetail.specificDates', { count: specialKeys.length })}
          </summary>
          <h4 className={`${styles.hoursSubheading} ${styles.hoursSubheadingSpaced}`}>
            {t('assetDetail.dateOverrides')}
          </h4>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('assetDetail.day')}</th>
                  <th>{t('assetDetail.hours')}</th>
                </tr>
              </thead>
              <tbody>
                {specialKeys.map((key) => (
                  <tr key={key}>
                    <td className={styles.specialDateKey}>{key}</td>
                    <td>{formatOpeningHoursValue(openingTimes[key], t)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      ) : null}
    </div>
  );
}

const ITEM_TYPE_KEY = 'Type';
const ITEM_KEY = 'Item';

const NAME_KEYS = ['name', 'addon_name', 'facility_name', 'title', 'label'] as const;
const ID_KEYS = ['addon_id', 'facility_id', 'id', 'ID'] as const;

function pickFirstRawName(row: Record<string, unknown>): string | undefined {
  for (const k of NAME_KEYS) {
    if (!(k in row)) {
      continue;
    }
    const v = row[k];
    if (typeof v === 'string' && v.trim()) {
      return v.trim();
    }
  }
  return undefined;
}

/** Single cell for facility / add-on: human-readable name only (IDs stay under Raw JSON). */
function formatFacilityAddonItemCell(
  row: Record<string, unknown>,
  t: TranslateFn,
  language: AppLanguage,
): string {
  const raw = pickFirstRawName(row);
  if (!raw) {
    return '—';
  }
  return localizeCatalogDisplayName(raw, language, catalogKindFromRow(row));
}

function isCollapsedIntoItemColumnKey(k: string): boolean {
  return (
    (NAME_KEYS as readonly string[]).includes(k) || (ID_KEYS as readonly string[]).includes(k)
  );
}

function collapseFacilityAddonRow(
  row: Record<string, unknown>,
  t: TranslateFn,
  language: AppLanguage,
): Record<string, unknown> {
  const item = formatFacilityAddonItemCell(row, t, language);
  const out: Record<string, unknown> = {
    [ITEM_TYPE_KEY]: row[ITEM_TYPE_KEY],
    [ITEM_KEY]: item,
  };
  for (const [k, v] of Object.entries(row)) {
    if (k === ITEM_TYPE_KEY || k === ITEM_KEY || isCollapsedIntoItemColumnKey(k) || isHiddenCodeColumnKey(k)) {
      continue;
    }
    out[k] = v;
  }
  return out;
}

function buildFacilityDisplayRows(
  facilities: unknown[] | undefined,
  addons: unknown[] | undefined,
  t: TranslateFn,
  language: AppLanguage,
): Record<string, unknown>[] {
  const merged = mergeFacilitiesAndPromotedAddonsForDisplay(facilities, addons);
  const rows = merged.map((f) => ({ [ITEM_TYPE_KEY]: 'Facility', ...f }));
  return rows.map((row) => collapseFacilityAddonRow(row, t, language));
}

function AssetPricingExVatNotice({ t }: { t: TranslateFn }) {
  return (
    <p className={styles.pricingExVatNotice} role="note">
      {t('assetProfile.pricesExcludeVat')}
    </p>
  );
}

function PricingTable({
  rows,
  leadingKeys = [],
  t,
  language,
  currency,
  reverseColumnsInHebrew = false,
}: {
  rows: Record<string, unknown>[];
  leadingKeys?: string[];
  t: TranslateFn;
  language: AppLanguage;
  currency: AssetLocaleCurrency;
  /** When Hebrew is active, show columns right-to-left (last column first). */
  reverseColumnsInHebrew?: boolean;
}) {
  const keySet = rows.reduce((acc, row) => {
    Object.keys(row).forEach((k) => acc.add(k));
    return acc;
  }, new Set<string>());
  const keys = [
    ...leadingKeys.filter((k) => keySet.has(k)),
    ...Array.from(keySet)
      .filter((k) => !leadingKeys.includes(k))
      .filter((k) => !isHiddenCodeColumnKey(k))
      .sort(),
  ];
  const displayKeys =
    reverseColumnsInHebrew && language === 'he' ? [...keys].reverse() : keys;
  if (!displayKeys.length) {
    return null;
  }
  const tableClass = [
    styles.table,
    reverseColumnsInHebrew && language === 'he' ? styles.tableRtlColumns : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={styles.tableWrap}>
      <table className={tableClass}>
        <thead>
          <tr>
            {displayKeys.map((k) => (
              <th key={k}>
                {k === ITEM_TYPE_KEY
                  ? t('assetDetail.type')
                  : k === ITEM_KEY
                    ? t('assetDetail.item')
                    : localizeAssetPriceFieldLabel(k, t, currency)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              {displayKeys.map((k) => (
                <td key={k}>{formatPricingTableCell(k, row[k], t, language, currency)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type AssetGetByIdViewProps = {
  data: Record<string, unknown>;
  theme?: 'dark' | 'light';
  /** Consumer-facing asset page (carousel, compact hours, book footer). */
  layout?: 'detail' | 'profile';
  /** Opens booking for this asset (Bookings tab quick-book flow). */
  onBookAsset?: () => void;
  bookDisabled?: boolean;
  bookDisabledTitle?: string;
  /** When the API omits owner logo on the asset, use the signed-in owner's profile logo. */
  ownerLogoOptions?: AssetRecordLogoOptions;
};

/** Touch / phone: single tap. Mouse: double-click (avoids opening while scrolling the page). */
function prefersTapToEnlargeImage(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false;
  }
  return window.matchMedia('(hover: none), (pointer: coarse)').matches;
}

function GalleryImageCard({
  src,
  alt,
  staticPlaceholder = false,
  viewLargerLabel,
  onOpen,
}: {
  src: string;
  alt: string;
  staticPlaceholder?: boolean;
  viewLargerLabel: string;
  onOpen?: () => void;
}) {
  const [failed, setFailed] = useState(false);
  const ignoreClickUntilRef = useRef(0);
  useEffect(() => {
    setFailed(false);
  }, [src]);
  const displaySrc = failed ? ASSET_OFFICE_PLACEHOLDER_SRC : src;
  const canOpen = Boolean(onOpen && !staticPlaceholder && !failed);

  const openEnlarged = useCallback(() => {
    onOpen?.();
  }, [onOpen]);

  const onEnlargeClick = useCallback(
    (event: MouseEvent) => {
      if (Date.now() < ignoreClickUntilRef.current) {
        return;
      }
      if (event.detail === 0 || prefersTapToEnlargeImage()) {
        openEnlarged();
      }
    },
    [openEnlarged],
  );

  const onEnlargeDoubleClick = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      ignoreClickUntilRef.current = Date.now() + 400;
      openEnlarged();
    },
    [openEnlarged],
  );

  const content = (
    <>
      <img
        src={displaySrc}
        alt={alt}
        className={styles.image}
        loading="lazy"
        decoding="async"
        draggable={false}
        onError={() => {
          if (!staticPlaceholder) {
            setFailed(true);
          }
        }}
      />
    </>
  );

  if (canOpen) {
    return (
      <button
        type="button"
        className={`${styles.imageCard} ${styles.imageCardEnlargeable}`}
        aria-label={viewLargerLabel}
        onClick={onEnlargeClick}
        onDoubleClick={onEnlargeDoubleClick}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={styles.imageCard} aria-label={alt}>
      {content}
    </div>
  );
}

/** First 8 hex chars of a UUID — list disambiguation only (detail view uses 8-digit id). */
export function formatShortAssetUuid(id: string): string {
  const t = id.trim().toLowerCase();
  if (!t) {
    return '';
  }
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(t)) {
    return t.slice(0, 8);
  }
  if (t.length > 12) {
    return `${t.slice(0, 8)}…`;
  }
  return t;
}

export function assetRecordDisplayTitle(data: Record<string, unknown>): string {
  return (
    pickVenueTitleCandidate(data.asset_name) ||
    pickVenueTitleCandidate(data.facility_name) ||
    pickVenueTitleCandidate(data.name) ||
    pickVenueTitleCandidate(data.AssetName) ||
    pickVenueTitleCandidate(data.FacilityName) ||
    pickVenueTitleCandidate(data.Name) ||
    pickVenueTitleCandidate(data.title) ||
    pickVenueTitleCandidate(data.Title) ||
    'Asset'
  );
}

/** List/browse title — asset name only (never appends or falls back to a raw id). */
export function assetRecordListTitle(data: Record<string, unknown>): string {
  return (
    pickVenueTitleCandidate(data.asset_name) ||
    pickVenueTitleCandidate(data.facility_name) ||
    pickVenueTitleCandidate(data.name) ||
    'Asset'
  );
}

const ASSET_ID_LABEL_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isAssetIdLikeLabel(value: string): boolean {
  const trimmed = value.trim();
  return ASSET_ID_LABEL_RE.test(trimmed) || /^[0-9a-f]{32}$/i.test(trimmed);
}

function pickVenueTitleCandidate(raw: unknown): string {
  if (typeof raw !== 'string') {
    return '';
  }
  const trimmed = raw.trim();
  if (!trimmed || isAssetIdLikeLabel(trimmed)) {
    return '';
  }
  return trimmed;
}

/** Venue label for booking authorization — never falls back to asset UUID. */
export function assetRecordVenueTitle(data: Record<string, unknown>): string {
  const candidates = [
    data.asset_name,
    data.facility_name,
    data.name,
    data.AssetName,
    data.FacilityName,
    data.Name,
    data.title,
    data.Title,
  ];
  for (const raw of candidates) {
    const picked = pickVenueTitleCandidate(raw);
    if (picked) {
      return picked;
    }
  }
  return '';
}

function assetRecordAddressSources(data: Record<string, unknown>): Record<string, unknown>[] {
  const sources: Record<string, unknown>[] = [];
  const add = (raw: unknown) => {
    if (isPlainObject(raw)) {
      sources.push(raw);
    }
  };
  add(data.address);
  add(data.Address);
  add(data.facility);
  add(data.Facility);
  add(data.asset);
  add(data.Asset);
  add(data.location);
  add(data.Location);
  sources.push(data);
  return sources;
}

function scalarAssetAddressLine(data: Record<string, unknown>): string | null {
  const keys = [
    'asset_address',
    'address_line',
    'full_address',
    'formatted_address',
    'location_address',
    'location',
  ] as const;
  for (const key of keys) {
    const raw = data[key];
    if (typeof raw === 'string' && raw.trim()) {
      return raw.trim();
    }
  }
  if (typeof data.address === 'string' && data.address.trim()) {
    return data.address.trim();
  }
  return null;
}

function pickTrimmedAddressString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function pickAddressBuilding(record: Record<string, unknown>): string {
  return (
    pickTrimmedAddressString(record.building) ||
    pickTrimmedAddressString(record.street_number) ||
    pickTrimmedAddressString(record.house_number)
  );
}

/** Street line with optional building/house number after the street name. */
function formatStreetWithBuilding(record: Record<string, unknown>): string | null {
  const street =
    pickTrimmedAddressString(record.street) || pickTrimmedAddressString(record.Street);
  if (!street) {
    return null;
  }
  const building = pickAddressBuilding(record);
  return building ? `${street} ${building}` : street;
}

function pickAddressZip(record: Record<string, unknown>): string | null {
  const zip =
    pickTrimmedAddressString(record.zip_code) ||
    pickTrimmedAddressString(record.zip) ||
    pickTrimmedAddressString(record.postal_code);
  return zip || null;
}

function addressPartsFromRecord(
  record: Record<string, unknown>,
  language: AppLanguage,
  zipFallback?: Record<string, unknown>,
  includeZip = false,
): string[] {
  const parts: string[] = [];
  const streetLine = formatStreetWithBuilding(record);
  if (streetLine) {
    parts.push(streetLine);
  }
  const line2 = record.address2 ?? record.address_2 ?? record.Address2;
  if (parts.length === 0 && typeof line2 === 'string' && line2.trim()) {
    parts.push(line2.trim());
  }
  const city = record.city ?? record.City;
  if (typeof city === 'string' && city.trim()) {
    parts.push(localizeGeoLabel(city.trim(), language));
  }
  if (includeZip) {
    const zip = pickAddressZip(record) || (zipFallback ? pickAddressZip(zipFallback) : null);
    if (zip) {
      parts.push(zip);
    }
  }
  if (parts.length === 0) {
    const state = record.state ?? record.state_name;
    if (typeof state === 'string' && state.trim()) {
      parts.push(localizeGeoLabel(state.trim(), language));
    }
    const country = record.country ?? record.country_name;
    if (typeof country === 'string' && country.trim()) {
      parts.push(localizeGeoLabel(country.trim(), language));
    }
  }
  return parts;
}

export function resolveConfirmationVenueDetails(
  sources: Array<Record<string, unknown> | null | undefined>,
  language: AppLanguage,
): { assetName: string; assetAddress: string | null } {
  let assetName = '';
  let assetAddress: string | null = null;

  for (const source of sources) {
    if (!source) {
      continue;
    }
    const name = assetRecordVenueTitle(source);
    const line = assetRecordListAddressLine(source, language);
    if (line) {
      assetAddress = line;
    }
    if (name && !assetName) {
      assetName = name;
    }
  }

  return { assetName, assetAddress };
}

function hasAddressStreetOrCity(record: Record<string, unknown>): boolean {
  if (formatStreetWithBuilding(record)) {
    return true;
  }
  const city = record.city ?? record.City;
  return typeof city === 'string' && city.trim().length > 0;
}

/** One-line address for asset list cards (street + city; no zip). */
export function assetRecordListAddressLine(
  data: Record<string, unknown>,
  language: AppLanguage,
): string | null {
  return assetRecordAddressLine(data, language, { includeZip: false });
}

/** One-line address for asset detail / profile view (includes zip when known). */
export function assetRecordDetailAddressLine(
  data: Record<string, unknown>,
  language: AppLanguage,
): string | null {
  return assetRecordAddressLine(data, language, { includeZip: true });
}

export type AssetRecordAddressLineOptions = {
  includeZip?: boolean;
};

function assetRecordAddressLine(
  data: Record<string, unknown>,
  language: AppLanguage,
  options: AssetRecordAddressLineOptions = {},
): string | null {
  const includeZip = options.includeZip === true;
  for (const record of assetRecordAddressSources(data)) {
    const parts = addressPartsFromRecord(record, language, data, includeZip);
    if (parts.length > 0 && hasAddressStreetOrCity(record)) {
      return parts.join(', ');
    }
  }

  const streetCity = assetRecordStreetCityLine(data, language, { includeZip });
  if (streetCity) {
    return streetCity;
  }

  const scalar = scalarAssetAddressLine(data);
  if (scalar) {
    if (includeZip) {
      const zip = pickAddressZip(data);
      if (zip && !scalar.includes(zip)) {
        return `${scalar}, ${zip}`;
      }
    }
    return scalar;
  }

  return assetRecordCityLine(data, language);
}

function collectStreetCityParts(
  record: Record<string, unknown>,
  language: AppLanguage,
  zipFallback?: Record<string, unknown>,
  includeZip = false,
): string[] {
  const parts: string[] = [];
  const streetLine = formatStreetWithBuilding(record);
  if (streetLine) {
    parts.push(streetLine);
  }
  const line2 = record.address2 ?? record.address_2;
  if (parts.length === 0 && typeof line2 === 'string' && line2.trim()) {
    parts.push(line2.trim());
  }
  const city = record.city;
  if (typeof city === 'string' && city.trim()) {
    parts.push(localizeGeoLabel(city.trim(), language));
  }
  if (includeZip) {
    const zip = pickAddressZip(record) || (zipFallback ? pickAddressZip(zipFallback) : null);
    if (zip) {
      parts.push(zip);
    }
  }
  return parts;
}

/** City only — home carousel second line. */
export function assetRecordCityLine(
  data: Record<string, unknown>,
  language: AppLanguage,
): string | null {
  const nested = data.address;
  const record = isPlainObject(nested) ? nested : data;
  const city = record.city;
  if (typeof city === 'string' && city.trim()) {
    return localizeGeoLabel(city.trim(), language);
  }
  return null;
}

/** Home/list street + city on one line (no district). */
export function assetRecordStreetCityLine(
  data: Record<string, unknown>,
  language: AppLanguage,
  options: AssetRecordAddressLineOptions = {},
): string | null {
  const includeZip = options.includeZip === true;
  const nested = data.address;
  const parts = isPlainObject(nested)
    ? collectStreetCityParts(nested, language, data, includeZip)
    : collectStreetCityParts(data, language, undefined, includeZip);
  if (parts.length === 0) {
    return null;
  }
  const record = isPlainObject(nested) ? nested : data;
  if (!hasAddressStreetOrCity(record)) {
    return null;
  }
  return parts.join(', ');
}

function pickDistrictLabelFromRecord(record: Record<string, unknown>): string | null {
  const scalarFields = [
    record.district_name,
    record.districtName,
    record.region_name,
    record.regionName,
    record.state_name,
    record.stateName,
  ];
  for (const field of scalarFields) {
    if (typeof field === 'string' && field.trim()) {
      return field.trim();
    }
  }
  for (const key of ['district', 'District', 'region', 'Region', 'state', 'State'] as const) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (isPlainObject(value)) {
      const nested =
        value.name ??
        value.label ??
        value.title ??
        value.district_name ??
        value.region_name ??
        value.state_name;
      if (typeof nested === 'string' && nested.trim()) {
        return nested.trim();
      }
    }
  }
  return null;
}

/** District / region label for compact home cards. */
export function assetRecordDistrictLine(
  data: Record<string, unknown>,
  language: AppLanguage,
): string | null {
  const sources: Record<string, unknown>[] = [];
  const nested = data.address;
  if (isPlainObject(nested)) {
    sources.push(nested);
  }
  sources.push(data);
  for (const key of ['facility', 'Facility', 'asset', 'region', 'district', 'state'] as const) {
    const nestedRecord = data[key];
    if (isPlainObject(nestedRecord)) {
      sources.push(nestedRecord);
    }
  }

  for (const record of sources) {
    const label = pickDistrictLabelFromRecord(record);
    if (label) {
      return localizeGeoLabel(label, language);
    }
  }
  return null;
}

/** Compact list label — name only (address and sub-title render on separate lines). */
export function assetRecordListLabel(data: Record<string, unknown>): string {
  return assetRecordListTitle(data);
}

export function assetRecordFirstImageSrc(data: Record<string, unknown>): string | null {
  const list = recordResolvedImageSrcs(data);
  return list[0] ?? null;
}

export function assetRecordLogoSrc(
  data: Record<string, unknown>,
  options?: AssetRecordLogoOptions,
): string {
  return resolveAssetRecordLogoSrc(data, options);
}

function assetRecordNavigationUrl(data: Record<string, unknown>, addressLine: string | null): string | null {
  const lat = data.latitude;
  const lng = data.longitude;
  if (typeof lat === 'number' && typeof lng === 'number' && Number.isFinite(lat) && Number.isFinite(lng)) {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }
  if (typeof lat === 'string' && typeof lng === 'string' && lat.trim() && lng.trim()) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat.trim()},${lng.trim()}`)}`;
  }
  if (addressLine) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressLine)}`;
  }
  return null;
}

/** Raw, human-readable asset type name carried directly on the record (if any). */
function assetRecordTypeName(data: Record<string, unknown>): string {
  const raw = data.asset_type_name ?? data.type_name;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : '';
}

/** Numeric asset type id on the record (if any). */
function assetRecordTypeId(data: Record<string, unknown>): number | null {
  const raw = data.asset_type_id ?? data.type_id;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Readable, localized asset type label. Uses `asset_type_name` when present, otherwise resolves
 * the numeric `asset_type_id` against the asset-types catalog (`GET /assets/asset-types`).
 */
function useResolvedAssetTypeLabel(
  data: Record<string, unknown>,
  language: AppLanguage,
  t: TranslateFn,
): string | null {
  const nameFromRecord = assetRecordTypeName(data);
  const typeId = assetRecordTypeId(data);
  const needCatalog = !nameFromRecord && typeId !== null;
  const [catalog, setCatalog] = useState<Map<number, string>>(() => new Map());

  useEffect(() => {
    if (!needCatalog) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const types = await fetchAssetTypesForBrowse();
        if (cancelled) {
          return;
        }
        const map = new Map<number, string>();
        for (const type of types) {
          if (typeof type.id === 'number') {
            map.set(type.id, type.label);
          }
        }
        setCatalog(map);
      } catch {
        /* leave type unresolved — header simply omits it */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [needCatalog]);

  if (nameFromRecord) {
    return localizeAssetTypeLabel(nameFromRecord, language, t);
  }
  if (typeId !== null) {
    const label = catalog.get(typeId);
    if (label) {
      return localizeAssetTypeLabel(label, language, t);
    }
  }
  return null;
}

function AssetProfileListingPricing({
  data,
  t,
  currency,
  language,
}: {
  data: Record<string, unknown>;
  t: TranslateFn;
  currency: AssetLocaleCurrency;
  language: AppLanguage;
}) {
  const { hourly, daily, monthly } = resolveListingHourlyDailyPrices(data);
  const textDir = language === 'he' ? 'rtl' : 'ltr';
  const formatPrice = (price: number | null) =>
    price !== null ? formatMoneyAmount(price, currency) : t('assetProfile.priceNotSet');

  return (
    <section className={styles.profilePricingSection} dir={textDir} aria-labelledby="asset-pricing-title">
      <h2 id="asset-pricing-title" className={styles.profileSectionTitle}>
        {labelWithCurrencySymbol(t('assetDetail.pricing'), currency)}
      </h2>
      <AssetPricingExVatNotice t={t} />
      <dl className={styles.profileListingGrid}>
        <div className={styles.profileListingRow}>
          <dt>{t('assetProfile.pricingHourly')}</dt>
          <dd>{formatPrice(hourly)}</dd>
        </div>
        {daily !== null ? (
          <div className={styles.profileListingRow}>
            <dt>{t('assetProfile.pricingDaily')}</dt>
            <dd>{formatPrice(daily)}</dd>
          </div>
        ) : null}
        {monthly !== null ? (
          <div className={styles.profileListingRow}>
            <dt>{t('assetProfile.pricingMonthly')}</dt>
            <dd>{formatPrice(monthly)}</dd>
          </div>
        ) : null}
      </dl>
    </section>
  );
}

export function AssetGetByIdView({
  data,
  theme = 'dark',
  layout = 'detail',
  onBookAsset,
  bookDisabled = false,
  bookDisabledTitle,
  ownerLogoOptions,
}: AssetGetByIdViewProps) {
  const { t, language } = useI18n();
  const [lightbox, setLightbox] = useState<{ src: string; caption: string } | null>(null);
  const assetCurrency = resolveAssetLocaleCurrency(data);
  const [facilityCatalogRows, setFacilityCatalogRows] = useState<Record<string, unknown>[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const raw = await fetchAssetFacilities();
        if (cancelled) {
          return;
        }
        const list = Array.isArray(raw)
          ? raw.filter((row): row is Record<string, unknown> => isPlainObject(row))
          : [];
        setFacilityCatalogRows(list);
      } catch {
        if (!cancelled) {
          setFacilityCatalogRows([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const title = assetRecordVenueTitle(data) || t('assetDetail.asset');
  const assetTypeLabel = useResolvedAssetTypeLabel(data, language, t);

  const resolvedGallerySrcs = recordResolvedImageSrcs(data);
  const imagePayloadNotice = assetRecordImagePayloadNotice(data);
  const capacityLine = assetRecordCapacityLine(data);
  const flatKeys = sortedKeys(data).filter((k) => !NESTED_SECTION_KEYS.has(k));

  function shouldRenderDetailKey(key: string): boolean {
    if (key === 'description' || key === 'asset_name' || key === 'name' || key === 'sub_title') {
      return false;
    }
    if (key === 'min_capacity' || key === 'max_capacity') {
      return false;
    }
    if (key === 'id' || key === 'asset_id') {
      return false;
    }
    if (assetTypeLabel && (key === 'asset_type_id' || key === 'asset_type_name' || key === 'type_id' || key === 'type_name')) {
      return false;
    }
    if (isImageDetailKey(key)) {
      return false;
    }
    const v = data[key];
    if (isPlainObject(v) || Array.isArray(v)) {
      return false;
    }
    return true;
  }

  const hasAnyDetailRow = flatKeys.some(shouldRenderDetailKey);

  const address = data.address;
  const openingTimes = resolveAssetOpeningTimesForDisplay(data);
  const facilities = data.facilities;
  const pricingMethods = data.pricing_methods;
  const addons = data.addons;
  const facilitiesForDisplay = enrichFacilitiesWithCatalog(
    mergeFacilitiesAndPromotedAddonsForDisplay(
      Array.isArray(facilities) ? facilities : undefined,
      Array.isArray(addons) ? addons : undefined,
    ),
    facilityCatalogRows,
  );
  const facilityDisplayRows = buildFacilityDisplayRows(
    Array.isArray(facilities) ? facilities : undefined,
    Array.isArray(addons) ? addons : undefined,
    t,
    language,
  );
  const facilityHighlights = pickFacilityAddonHighlights(facilitiesForDisplay, undefined, language, t);

  const headerId = assetRecordShortDisplayId(data);
  const headerIdFull = assetRecordPrimaryId(data);
  const subTitle = assetRecordSubTitle(data);
  const addressLine = assetRecordDetailAddressLine(data, language);
  const logoSrc = assetRecordLogoSrc(data, ownerLogoOptions);
  const email = assetListingEmail(data);
  const navUrl = assetRecordNavigationUrl(data, addressLine);

  const rootClass = [
    styles.root,
    theme === 'light' ? styles.lightTheme : '',
    layout === 'profile' ? styles.profileRoot : '',
    layout === 'profile' && onBookAsset ? styles.profileRootHasBookBar : '',
  ]
    .filter(Boolean)
    .join(' ');

  const gallerySection = (
    <section className={styles.gallery} aria-label={t('assetDetail.assetImages')}>
      <h3 className={styles.galleryTitle}>{t('assetDetail.images')}</h3>
      {imagePayloadNotice ? (
        <p className={styles.imagePayloadNotice} role="status">
          {t('assetDetail.imagesWireError')}
        </p>
      ) : null}
      <div className={styles.galleryGrid}>
        {resolvedGallerySrcs.length > 0 ? (
          resolvedGallerySrcs.map((src, i) => (
            <GalleryImageCard
              key={`${src}-${i}`}
              src={src}
              alt={t('assetDetail.assetImages')}
              viewLargerLabel={t('assetDetail.viewLarger')}
              onOpen={() => setLightbox({ src, caption: '' })}
            />
          ))
        ) : (
          <GalleryImageCard
            src={ASSET_OFFICE_PLACEHOLDER_SRC}
            alt={t('assetDetail.imageNotAvailable')}
            staticPlaceholder
            viewLargerLabel={t('assetDetail.viewLarger')}
          />
        )}
      </div>
    </section>
  );

  const assetIdHeader =
    headerId !== null ? (
      <p className={styles.idLine}>
        <span className={styles.idLabel}>{t('assetField.id')}</span>
        <code className={styles.idValue} title={headerIdFull ?? undefined}>
          {headerId}
        </code>
      </p>
    ) : null;

  const assetTypeChip =
    assetTypeLabel !== null ? (
      <span className={styles.assetTypeChip}>
        <AssetTypeIcon label={assetTypeLabel} className={styles.assetTypeIcon} />
        <span className={styles.assetTypeChipLabel}>{assetTypeLabel}</span>
      </span>
    ) : null;

  const profileIdTypeRow =
    headerId !== null || assetTypeLabel !== null ? (
      <div className={styles.profileIdTypeRow}>
        {assetIdHeader}
        {assetTypeChip}
      </div>
    ) : null;

  const expandedDetails = (
    <>
      {headerId !== null || assetTypeLabel ? (
        <header className={styles.header}>
          {assetIdHeader}
          {assetTypeLabel ? <p className={styles.typeLine}>{assetTypeLabel}</p> : null}
        </header>
      ) : null}
      {capacityLine !== null || hasAnyDetailRow ? (
        <section className={styles.subsection}>
          <h3 className={styles.subsectionTitle}>{t('assetDetail.details')}</h3>
          <dl className={styles.detailGrid}>
            {capacityLine !== null ? (
              <div key="capacity-combined" className={styles.detailRow}>
                <dt>{t('assetDetail.capacity')}</dt>
                <dd>{capacityLine}</dd>
              </div>
            ) : null}
            {flatKeys.map((key) => {
              if (!shouldRenderDetailKey(key)) {
                return null;
              }
              const v = data[key];
              return (
                <div key={key} className={styles.detailRow}>
                  <dt>{localizeAssetPriceFieldLabel(key, t, assetCurrency)}</dt>
                  <dd>{formatAssetDetailValue(key, v, t, language, assetCurrency)}</dd>
                </div>
              );
            })}
          </dl>
        </section>
      ) : null}
      {isPlainObject(address) ? <AddressBlock address={address} zipFallback={data} /> : null}
      {facilityDisplayRows.length > 0 ? (
        <div className={styles.subsection}>
          <h3 className={styles.subsectionTitle}>{t('assetDetail.facilitiesAddons')}</h3>
          <PricingTable
            rows={facilityDisplayRows}
            leadingKeys={[ITEM_TYPE_KEY, ITEM_KEY]}
            t={t}
            language={language}
            currency={assetCurrency}
          />
        </div>
      ) : null}
      {Array.isArray(pricingMethods) && pricingMethods.length > 0 && pricingMethods.every(isPlainObject) ? (
        <div className={styles.subsection}>
          <h3 className={styles.subsectionTitle}>
            {labelWithCurrencySymbol(t('assetDetail.pricing'), assetCurrency)}
          </h3>
          <AssetPricingExVatNotice t={t} />
          <PricingTable
            rows={pricingMethods as Record<string, unknown>[]}
            t={t}
            language={language}
            currency={assetCurrency}
            reverseColumnsInHebrew
          />
        </div>
      ) : null}
      {openingTimes ? <OpeningTimesBlock openingTimes={openingTimes} /> : null}
    </>
  );

  if (layout === 'profile') {
    const textDir = language === 'he' ? 'rtl' : 'ltr';
    const identityCol = (
      <div className={styles.profileIdentityCol} dir={textDir}>
        <div className={styles.profileIdentityText}>
          <h1 className={styles.profileTitle}>{title}</h1>
          {subTitle ? <p className={styles.headerSubTitle}>{subTitle}</p> : null}
          {addressLine ? <p className={styles.profileAddress}>{addressLine}</p> : null}
        </div>
        <div className={styles.profileLogoFrame}>
          <img src={logoSrc} alt="" className={styles.profileLogo} />
        </div>
      </div>
    );
    const hoursCol = (
      <div className={styles.profileHoursCol} dir={textDir}>
        <AssetProfileActionIcons
          title={title}
          email={email}
          addressLine={addressLine}
          navUrl={navUrl}
          shareUrl={buildAssetBrowseShareUrl(headerIdFull ?? '')}
          shareLabel={t('assetProfile.share')}
          shareCopyLabel={t('assetProfile.shareCopy')}
          shareWhatsAppLabel={t('assetProfile.shareWhatsApp')}
          shareEmailLabel={t('assetProfile.shareEmail')}
          shareCopiedLabel={t('assetProfile.shareCopied')}
          navigateLabel={t('assetProfile.navigate')}
          emailLabel={t('assetProfile.email')}
          emailGmailLabel={t('assetProfile.emailGmail')}
          emailAppLabel={t('assetProfile.emailApp')}
        />
        {openingTimes ? <CompactOpeningHours openingTimes={openingTimes} /> : null}
      </div>
    );

    return (
      <div className={rootClass}>
        {profileIdTypeRow ? <div className={styles.profileTopId}>{profileIdTypeRow}</div> : null}
        <div className={styles.profileDesktopStage}>
          <div className={styles.profileHeroWrap}>
            <AssetImageDistanceOverlay
              asset={data}
              theme={theme === 'light' ? 'light' : 'dark'}
              badgeClassName={styles.profileDistanceBadge}
            >
              <AssetImageHeroCarousel
                className={styles.profileHeroCarousel}
                overlayChrome
                images={resolvedGallerySrcs}
                alt={title}
                onImageClick={(src) => setLightbox({ src, caption: '' })}
              />
            </AssetImageDistanceOverlay>
            <FavoriteAssetHeartButton
              asset={data}
              className={styles.profileFavoriteHeart}
              activeClassName={styles.profileFavoriteHeartActive}
            />
          </div>

          <div className={styles.profileDesktopAside}>
            <div className={styles.profileIdentityGrid}>
              {hoursCol}
              {identityCol}
            </div>

            {facilityHighlights.length > 0 ? (
              <section
                className={styles.profileFacilities}
                aria-label={t('assetDetail.facilitiesAddons')}
              >
                <ul className={styles.profileFacilitiesList}>
                  {facilityHighlights.map((item, index) => (
                    <li
                      key={`${item.kind}-${item.label}-${index}`}
                      className={styles.profileFacilityItem}
                    >
                      <span className={styles.profileFacilityLabel}>{item.label}</span>
                      <AssetFacilityIcon iconId={item.iconId} />
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {typeof data.description === 'string' && data.description.trim() ? (
              <section className={styles.profileAbout}>
                <h2 className={styles.profileAboutTitle}>{t('assetProfile.about')}</h2>
                <p className={styles.profileAboutText}>{data.description.trim()}</p>
              </section>
            ) : null}

            <AssetProfileListingPricing
              data={data}
              t={t}
              currency={assetCurrency}
              language={language}
            />
          </div>
        </div>

        {onBookAsset ? (
          <div className={styles.profileBookBar}>
            <div className={styles.profileBookBarLegacy}>
              <button
                type="button"
                className={styles.profileBookButton}
                disabled={bookDisabled}
                title={bookDisabledTitle}
                onClick={onBookAsset}
              >
                {t('list.book')}
              </button>
            </div>
          </div>
        ) : null}

        {lightbox ? (
          <ImageLightbox
            src={lightbox.src}
            caption={lightbox.caption}
            closeLabel={t('assetDetail.closeLightbox')}
            onClose={() => setLightbox(null)}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className={rootClass}>
      <header className={styles.header}>
        {(headerId !== null || assetTypeLabel !== null) ? (
          <div className={styles.profileIdTypeRow}>{assetIdHeader}{assetTypeChip}</div>
        ) : null}
        <h2 className={styles.title}>{title}</h2>
        {subTitle ? <p className={styles.headerSubTitle}>{subTitle}</p> : null}
      </header>

      {gallerySection}

      {typeof data.description === 'string' && data.description.trim() ? (
        <section className={styles.descriptionSection}>
          <h3 className={styles.subsectionTitle}>{t('assetDetail.description')}</h3>
          <p className={styles.description}>{data.description.trim()}</p>
        </section>
      ) : null}

      {capacityLine !== null || hasAnyDetailRow ? (
        <section className={styles.subsection}>
          <h3 className={styles.subsectionTitle}>{t('assetDetail.details')}</h3>
          <dl className={styles.detailGrid}>
            {capacityLine !== null ? (
              <div key="capacity-combined" className={styles.detailRow}>
                <dt>{t('assetDetail.capacity')}</dt>
                <dd>{capacityLine}</dd>
              </div>
            ) : null}
            {flatKeys.map((key) => {
              if (!shouldRenderDetailKey(key)) {
                return null;
              }
              const v = data[key];
              return (
                <div key={key} className={styles.detailRow}>
                  <dt>{localizeAssetPriceFieldLabel(key, t, assetCurrency)}</dt>
                  <dd>{formatAssetDetailValue(key, v, t, language, assetCurrency)}</dd>
                </div>
              );
            })}
          </dl>
        </section>
      ) : null}

      {isPlainObject(address) ? <AddressBlock address={address} zipFallback={data} /> : null}

      {facilityDisplayRows.length > 0 ? (
        <div className={styles.subsection}>
          <h3 className={styles.subsectionTitle}>{t('assetDetail.facilitiesAddons')}</h3>
          <PricingTable
            rows={facilityDisplayRows}
            leadingKeys={[ITEM_TYPE_KEY, ITEM_KEY]}
            t={t}
            language={language}
            currency={assetCurrency}
          />
        </div>
      ) : null}

      {Array.isArray(pricingMethods) && pricingMethods.length > 0 && pricingMethods.every(isPlainObject) ? (
        <div className={styles.subsection}>
          <h3 className={styles.subsectionTitle}>
            {labelWithCurrencySymbol(t('assetDetail.pricing'), assetCurrency)}
          </h3>
          <AssetPricingExVatNotice t={t} />
          <PricingTable
            rows={pricingMethods as Record<string, unknown>[]}
            t={t}
            language={language}
            currency={assetCurrency}
            reverseColumnsInHebrew
          />
        </div>
      ) : null}

      {openingTimes ? <OpeningTimesBlock openingTimes={openingTimes} /> : null}

      {lightbox ? (
        <ImageLightbox
          src={lightbox.src}
          caption={lightbox.caption}
          closeLabel={t('assetDetail.closeLightbox')}
          onClose={() => setLightbox(null)}
        />
      ) : null}
    </div>
  );
}

export function canRenderAssetGetByIdView(data: unknown): data is Record<string, unknown> {
  return isPlainObject(data);
}
